<template>
  <div v-if="status || !collapse" class="h-4 w-4 flex-none">
    <icon-base
      v-if="status"
      v-tooltip="status.tooltip"
      :icon="status.icon"
      :class="status.color"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { formatDateCustom } from "@/helpers/formatting";

import IconX from "@/components/icons/deals/IconX.vue";
import IconBounced from "@/components/icons/email/IconBounced.vue";
import IconComplainedAt from "@/components/icons/email/IconComplainedAt.vue";
import IconDelivered from "@/components/icons/email/IconDelivered.vue";
import IconOpened from "@/components/icons/email/IconOpened.vue";
import IconSent from "@/components/icons/email/IconSent.vue";
import IconBase from "@/components/ui/IconBase.vue";

interface Props {
  email: {
    complaint_at: string | null;
    bounced_at: string | null;
    opened_at: string | null;
    delivered_at: string | null;
    status: string | null;
  };
  collapse?: boolean;
}

const { email, collapse } = defineProps<Props>();

const { t } = useI18n();

const status = computed(() => {
  if (email.complaint_at) {
    return {
      icon: IconComplainedAt,
      color: "text-red-500",
      tooltip: t("EMAIL_INTEGRATION.STATUSES.COMPLAINED_AT", {
        date: formatDateCustom(
          email.complaint_at,
          "MMMM dd, yyyy hh:mm a",
          true
        ),
      }),
    };
  }

  if (email.bounced_at) {
    return {
      icon: IconBounced,
      color: "text-yellow-500",
      tooltip: t("EMAIL_INTEGRATION.STATUSES.BOUNCED", {
        date: formatDateCustom(email.bounced_at, "MMMM dd, yyyy hh:mm a", true),
      }),
    };
  }

  if (email.opened_at) {
    return {
      icon: IconOpened,
      color: "text-green-500",
      tooltip: t("EMAIL_INTEGRATION.STATUSES.OPENED", {
        date: formatDateCustom(email.opened_at, "MMMM dd, yyyy hh:mm a", true),
      }),
    };
  }

  if (email.delivered_at) {
    return {
      icon: IconDelivered,
      color: "text-blue-500",
      tooltip: t("EMAIL_INTEGRATION.STATUSES.DELIVERED", {
        date: formatDateCustom(
          email.delivered_at,
          "MMMM dd, yyyy hh:mm a",
          true
        ),
      }),
    };
  }

  if (email.status === "failed") {
    return {
      icon: IconX,
      color: "text-red-500",
      tooltip: t("EMAIL_INTEGRATION.STATUSES.FAILED"),
    };
  }

  if (email.status === "sent") {
    return {
      icon: IconSent,
      color: "text-gray-500",
      tooltip: t("EMAIL_INTEGRATION.STATUSES.SENT"),
    };
  }

  return null;
});
</script>
